import {
	Button,
	Grid as MUIGrid,
	Stack,
	Box,
	Paper,
	Typography,
	Chip,
	CircularProgress,
	useTheme,
	Tabs,
	Tab,
	Tooltip,
	Fade,
	Slider,
	Alert,
	Badge,
} from '@mui/material'; // Updated imports
import { getDatesBetween, getOccasionsFromActivities } from '../../helper/Occasions';
import { applyFilters, FilterState } from './filters';
import { ColumnHeader } from './ColumnHeader';
import { Activity, Facility, Occasion } from '@oskarjiang/matchi-types';
import { Card } from './Card';
import { useDrawer } from '../../contexts/DrawerContext';
import { useFocusedActivity } from '../../contexts/FocusedActivityContext';
import { useFocusedOccasion } from '../../contexts/FocusedOccasionContext';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { baseUrl } from '../../constants/endpoints';
import { getAllFacilityIds } from '../../helper/Facilities';
// New imports for icons
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import PersonIcon from '@mui/icons-material/Person';
import SportsIcon from '@mui/icons-material/Sports';
import PlaceIcon from '@mui/icons-material/Place';
import FemaleIcon from '@mui/icons-material/Female';
import ElderlyIcon from '@mui/icons-material/Elderly';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useHiddenFacilities } from './useHiddenFacilities';
import { useShowOnlyEmpty } from './useShowOnlyEmpty';
import { useShowAfterWork } from './useShowAfterWork';
import { usePrivateLesson } from './usePrivateLesson';
import { useShowMachine } from './useShowMachine';
import { useHideLateEvening } from './useHideLateEvening';
import { useMinLevel, DEFAULT_MIN_LEVEL } from './useMinLevel';
import { useLadiesOnly } from './useLadiesOnly';
import { useSeniorOnly } from './useSeniorOnly';
import { useMixedOnly } from './useMixedOnly';

type Props = {
	searchTerm: string;
	activities: Activity[] | undefined;
	relativeWeeks: number;
	loading: boolean;
};

export const Grid = ({ searchTerm, activities, relativeWeeks, loading }: Props) => {
	const theme = useTheme();
	const { setOpen } = useDrawer();
	const { setFocusedOccasion } = useFocusedOccasion();
	const { setFocusedActivity } = useFocusedActivity();
	const handleOpen = async (occasion: Occasion, activity: Activity) => {
		setFocusedActivity(activity);
		setFocusedOccasion(occasion);
		setOpen(true);
	};
	const { showOnlyEmpty, setShowOnlyEmpty } = useShowOnlyEmpty();
	const { showAfterWork, setShowAfterWork } = useShowAfterWork();
	const { ladiesOnly, setLadiesOnly } = useLadiesOnly();
	const { seniorOnly, setSeniorOnly } = useSeniorOnly();
	const { mixedOnly, setMixedOnly } = useMixedOnly();
	const { privateLesson, setPrivateLesson } = usePrivateLesson();
	const { showMachine, setShowMachine } = useShowMachine();
	const { hideLateEvening, setHideLateEvening } = useHideLateEvening();
	const { minLevel, setMinLevel } = useMinLevel();
	const { hiddenFacilities, setHiddenFacilities } = useHiddenFacilities();
	const [filterTab, setFilterTab] = useState<number>(0);
	const { data: facilities } = useQuery<Facility[]>({
		queryKey: ['facilities'],
		queryFn: () => fetch(`${baseUrl}/facilities`).then((res) => res.json()),
		staleTime: 7 * 24 * 60 * 60 * 1000, // 1 week - rarely changes
	});

	// Handle filter tab change
	const handleFilterTabChange = (event: React.SyntheticEvent, newValue: number) => {
		setFilterTab(newValue);
	};

	const activeQuickFilterCount = [
		showOnlyEmpty,
		showAfterWork,
		ladiesOnly,
		seniorOnly,
		mixedOnly,
		privateLesson,
		showMachine,
		hideLateEvening,
		minLevel !== DEFAULT_MIN_LEVEL,
	].filter(Boolean).length;
	const hasActiveQuickFilters = activeQuickFilterCount > 0;

	const resetQuickFilters = () => {
		setShowOnlyEmpty(false);
		setShowAfterWork(false);
		setLadiesOnly(false);
		setSeniorOnly(false);
		setMixedOnly(false);
		setPrivateLesson(false);
		setShowMachine(false);
		setHideLateEvening(false);
		setMinLevel(DEFAULT_MIN_LEVEL);
	};

	if (loading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
				<CircularProgress color="primary" />
			</Box>
		);
	}

	const filterState: FilterState = {
		showOnlyEmpty,
		showAfterWork,
		ladiesOnly,
		seniorOnly,
		mixedOnly,
		privateLesson,
		showMachine,
		hideLateEvening,
		minLevel,
		searchTerm,
		hiddenFacilities,
	};

	// Get occasions for each date in the visible week and apply all filters
	const columns = getDatesBetween(0 + relativeWeeks * 7, 6 + relativeWeeks * 7).map((date) => ({
		date,
		filteredOccasions: applyFilters(getOccasionsFromActivities(activities, date), activities, filterState),
	}));

	return (
		<Fade in={!loading} timeout={500}>
			<Stack spacing={3}>
				{/* Filters Paper Container */}
				<Paper
					elevation={0}
					sx={{
						p: 2,
						borderRadius: 2,
						backgroundColor: theme.palette.background.paper,
					}}
				>
					<Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
						<Tabs
							value={filterTab}
							onChange={handleFilterTabChange}
							textColor="primary"
							indicatorColor="primary"
							variant="scrollable"
							scrollButtons="auto"
						>
							<Tab
								label={
									<Box sx={{ display: 'flex', alignItems: 'center' }}>
										<Badge badgeContent={activeQuickFilterCount} color="primary" sx={{ mr: 1.5 }}>
											<FilterAltIcon sx={{ fontSize: 18 }} />
										</Badge>
										<Typography variant="body2">Quick Filters</Typography>
									</Box>
								}
							/>
							<Tab
								label={
									<Box sx={{ display: 'flex', alignItems: 'center' }}>
										<Badge badgeContent={hiddenFacilities.length} color="primary" sx={{ mr: 1.5 }}>
											<PlaceIcon sx={{ fontSize: 18 }} />
										</Badge>
										<Typography variant="body2">Locations</Typography>
									</Box>
								}
							/>
						</Tabs>
					</Box>

					{/* Filter Tab Panels */}
					{filterTab === 0 && (
						<Box>
							{/* Filter Chips */}
							<Box sx={{ mb: 3 }}>
								<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
									<Typography variant="subtitle2" fontWeight={600}>
										Filter Options
									</Typography>
									<Tooltip title="Clear all quick filters and reset min level">
										<span>
											<Button
												size="small"
												variant="text"
												startIcon={<RestartAltIcon />}
												onClick={resetQuickFilters}
												disabled={!hasActiveQuickFilters}
											>
												Reset
											</Button>
										</span>
									</Tooltip>
								</Box>
								<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
									<Tooltip title="Show only slots with no participants">
										<Chip
											icon={<PersonIcon />}
											label="Only Empty"
											color={showOnlyEmpty ? 'primary' : 'default'}
											onClick={() => setShowOnlyEmpty(!showOnlyEmpty)}
											variant={showOnlyEmpty ? 'filled' : 'outlined'}
										/>
									</Tooltip>

									<Tooltip title="Filter for after work hours (16:00 - 19:00)">
										<Chip
											icon={<AccessTimeFilledIcon />}
											label="After Work"
											color={showAfterWork ? 'primary' : 'default'}
											onClick={() => setShowAfterWork(!showAfterWork)}
											variant={showAfterWork ? 'filled' : 'outlined'}
										/>
									</Tooltip>

									<Tooltip title="Show sessions targeted to ladies">
										<Chip
											icon={<FemaleIcon />}
											label="Ladies"
											color={ladiesOnly ? 'primary' : 'default'}
											onClick={() => setLadiesOnly(!ladiesOnly)}
											variant={ladiesOnly ? 'filled' : 'outlined'}
										/>
									</Tooltip>

									<Tooltip title="Show senior sessions">
										<Chip
											icon={<ElderlyIcon />}
											label="Senior"
											color={seniorOnly ? 'primary' : 'default'}
											onClick={() => setSeniorOnly(!seniorOnly)}
											variant={seniorOnly ? 'filled' : 'outlined'}
										/>
									</Tooltip>

									<Tooltip title="Show mixed sessions">
										<Chip
											icon={<Diversity3Icon />}
											label="Mixed"
											color={mixedOnly ? 'primary' : 'default'}
											onClick={() => setMixedOnly(!mixedOnly)}
											variant={mixedOnly ? 'filled' : 'outlined'}
										/>
									</Tooltip>

									<Tooltip title="Show private lessons only">
										<Chip
											icon={<PersonIcon />}
											label="Lessons"
											color={privateLesson ? 'primary' : 'default'}
											onClick={() => setPrivateLesson(!privateLesson)}
											variant={privateLesson ? 'filled' : 'outlined'}
										/>
									</Tooltip>

									<Tooltip title="Include machine sessions">
										<Chip
											icon={<SportsIcon />}
											label="Show Machine"
											color={showMachine ? 'primary' : 'default'}
											onClick={() => setShowMachine(!showMachine)}
											variant={showMachine ? 'filled' : 'outlined'}
										/>
									</Tooltip>

									<Tooltip title="Hide sessions that end 21:00 or later">
										<Chip
											icon={<NightsStayIcon />}
											label="Ends Before 21:00"
											color={hideLateEvening ? 'primary' : 'default'}
											onClick={() => setHideLateEvening(!hideLateEvening)}
											variant={hideLateEvening ? 'filled' : 'outlined'}
										/>
									</Tooltip>
								</Stack>
								<Box sx={{ mt: 1.5, maxWidth: 280 }}>
									<Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
										Min Level
									</Typography>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
										<Slider
											value={minLevel}
											onChange={(_, value) => {
												if (typeof value === 'number') {
													setMinLevel(Math.max(0, Math.min(10, value)));
												}
											}}
											min={0}
											max={10}
											step={1}
											marks
											valueLabelDisplay="auto"
											size="small"
											sx={{ flexGrow: 1, pt: 2 }}
										/>
										<Chip label={`Level ${minLevel}`} size="small" color="primary" variant="outlined" />
									</Box>
								</Box>
							</Box>
						</Box>
					)}

					{filterTab === 1 && (
						<Box>
							<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
								<Typography variant="subtitle2">Available Locations</Typography>
								<Button
									size="small"
									variant="text"
									onClick={() =>
										getAllFacilityIds(activities ?? []).every((facilityId) => hiddenFacilities.includes(facilityId))
											? setHiddenFacilities([])
											: setHiddenFacilities(getAllFacilityIds(activities ?? []))
									}
								>
									{`${
										getAllFacilityIds(activities ?? []).every((facilityId) => hiddenFacilities.includes(facilityId)) ? 'Show' : 'Hide'
									} All`}
								</Button>
							</Box>

							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
								{facilities
									?.filter((facility) => getAllFacilityIds(activities ?? []).includes(facility.id))
									.map((facility) => (
										<Chip
											key={facility.id}
											label={facility.name}
											color={
												getAllFacilityIds(activities ?? [])
													.filter((facilityId) => !hiddenFacilities.includes(facilityId))
													.includes(facility.id)
													? 'primary'
													: 'default'
											}
											variant={
												getAllFacilityIds(activities ?? [])
													.filter((facilityId) => !hiddenFacilities.includes(facilityId))
													.includes(facility.id)
													? 'filled'
													: 'outlined'
											}
											onClick={() =>
												hiddenFacilities.includes(facility.id)
													? setHiddenFacilities([...hiddenFacilities].filter((facilityId) => facilityId !== facility.id))
													: setHiddenFacilities([...hiddenFacilities].concat(facility.id))
											}
										/>
									))}
							</Box>
						</Box>
					)}
				</Paper>

				{/* Empty state when filters/search hide every session this week */}
				{!!activities?.length && columns.every(({ filteredOccasions }) => filteredOccasions.length === 0) && (
					<Alert
						severity="info"
						action={
							hasActiveQuickFilters ? (
								<Button color="inherit" size="small" onClick={resetQuickFilters}>
									Reset filters
								</Button>
							) : undefined
						}
						sx={{ mb: 2 }}
					>
						{hasActiveQuickFilters && searchTerm
							? `No sessions match your filters and search for "${searchTerm}".`
							: searchTerm
							? `No sessions match your search for "${searchTerm}".`
							: 'No sessions match your current filters.'}
					</Alert>
				)}

				{/* Weekly Schedule Grid */}
				<MUIGrid container>
					{columns.map(({ date, filteredOccasions }) => (
						<MUIGrid container direction="column" item={true} xs={1} columns={{ xs: 1, sm: 2, md: 3, lg: 4, xl: 7 }} key={date.getTime()}>
							<ColumnHeader dateToView={date} loading={loading} sessionCount={filteredOccasions.length} />
							{filteredOccasions.map((occasion) => {
								const relatedActivity = activities?.find((activity) => activity.id === occasion.activityId);
								return (
									relatedActivity && (
										<Card
											loading={loading}
											activity={relatedActivity}
											occasion={occasion}
											key={occasion.id}
											handleClick={() => handleOpen(occasion, relatedActivity)}
										/>
									)
								);
							})}
						</MUIGrid>
					))}
				</MUIGrid>
			</Stack>
		</Fade>
	);
};
